// İSG Portal — Storage / Doküman Yönetimi (Katman 8) API çağrıları + tipleri +
// **istemci yükleme orkestrasyonu** (FE'de ilk dosya-yükleme özelliği).
//
// KAYNAK: api-contract.md §8 (Storage). Backend kanonu; burada **varsayım YOK** —
// endpoint yolları/alanları ve yaşam döngüsü doğrudan sözleşmedendir. Serileştirme
// camelCase (§1); yeni kimlik `value.document.id`/`value.document.uuid` (§4a).
//
// **Akış (§8).** İçerik **sunucudan geçmez** — istemci presigned URL ile doğrudan S3'e
// yükler; API yalnız metadata + yaşam döngüsünü yönetir:
//   1. `POST /documents` → metadata (`pending`) + presigned PUT URL.
//   2. İstemci içeriği `uploadUrl`'e **PUT** eder (doğrudan S3'e — api client DEĞİL).
//   3. `POST /documents/{uuid}/confirm` → S3 HEAD ile doğrular → `confirmed`.
//   4. `GET /documents/{uuid}` → presigned GET (süreli indirme).
//   5. `DELETE /documents/{uuid}` → soft-delete + S3 nesnesi silinir.
//
// `uploadDocument` bu üç adımı (create → PUT → confirm) tek çağrıda sarmalar ve
// **yükleme ilerlemesini** (`onProgress`) raporlar. Durum wire'da küçük-harf string
// (`pending`/`confirmed`, §8). Tenant izolasyonu token'dan; istemci `companyId` göndermez.

import { api } from './client';
import type { Empty } from './envelope';
import { ApiError, isDefinitiveRejection } from './errors';

/** Doküman yaşam döngüsü durumu — wire'da küçük-harf string (§8). */
export type DocumentStatus = 'pending' | 'confirmed';

/** Doküman üst-verisi — api-contract §8 (`DocumentDto`). Zamanlar UTC ISO (§4). */
export interface DocumentDto {
  id: number;
  uuid: string;
  fileName: string;
  contentType: string;
  size: number;
  status: DocumentStatus;
  branchId: number | null;
  siteId: number | null;
  confirmedAt: string | null;
  createdAt: string | null;
}

/**
 * Doküman oluşturma yanıtı — api-contract §8.1 (`DocumentUploadDto`). Yeni kimlik
 * `document.id`/`document.uuid`; `uploadUrl` kısa ömürlü presigned PUT hedefidir.
 */
export interface DocumentUploadDto {
  document: DocumentDto;
  uploadUrl: string;
  uploadMethod: string;
  uploadUrlExpiresAt: string;
}

/** Doküman indirme yanıtı — api-contract §8.3 (`DocumentDownloadDto`). */
export interface DocumentDownloadDto {
  document: DocumentDto;
  downloadUrl: string;
  downloadUrlExpiresAt: string;
}

/**
 * Doküman metadata oluşturma isteği — api-contract §8.1 (`POST /documents`).
 * `size` bayt (> 0); `branchId`/`siteId` opsiyonel kapsam marker'ı (§4b).
 */
export interface DocumentCreateRequest {
  fileName: string;
  contentType: string;
  size: number;
  branchId?: number | null;
  siteId?: number | null;
}

// ---- İstemci-tarafı dosya politikası (FE ön-doğrulaması; backend de dayatır) ----
//
// Backend azami boyutu `Settings:Storage:*`'tan gelir (§8; `DOCUMENT_TOO_LARGE`). İstemci
// gereksiz yük/bekleme yaşatmamak için önden makul bir sınır + tür allow-list uygular.
// İSG uygunluk belgeleri: PDF + ofis dökümanı + görsel (taranmış SGK/sözleşme/sertifika).

/** İstemci ön-doğrulama azami boyutu (20 MB). Backend nihai otoritedir. */
export const DOCUMENT_MAX_SIZE_BYTES = 20 * 1024 * 1024;

/** Kabul edilen içerik türleri (uygunluk belgeleri). Boş/tanınmaz tip → allow-list dışı. */
export const DOCUMENT_ACCEPTED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

/** `<input accept>` için türetilmiş dize (tür + yaygın uzantılar). */
export const DOCUMENT_ACCEPT_ATTR =
  '.pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,application/pdf,image/*';

/** İstemci dosya ön-doğrulaması — hata kodu döner (i18n) ya da `null` (geçerli). */
export function validateDocumentFile(
  file: File,
): 'DOCUMENT_TOO_LARGE' | 'DOCUMENT_TYPE_UNSUPPORTED' | null {
  if (file.size <= 0 || file.size > DOCUMENT_MAX_SIZE_BYTES) {
    return 'DOCUMENT_TOO_LARGE';
  }
  // Tip boşsa (bazı tarayıcılar) uzantıya güvenip backend'e bırakırız; tanınıyorsa allow-list.
  if (
    file.type &&
    !DOCUMENT_ACCEPTED_TYPES.includes(file.type as (typeof DOCUMENT_ACCEPTED_TYPES)[number])
  ) {
    return 'DOCUMENT_TYPE_UNSUPPORTED';
  }
  return null;
}

// ---- Doküman Analizi (doc-check) okunabilirlik + toplu yükleme sınırları (§20) ----
//
// `DOCUMENT_ACCEPTED_TYPES` (9 tür) §8 genel yükleme allow-list'idir; doc-check'in intake
// katmanı (`services/doc-check/src/intake/types.ts`, `IntakeMediaType`) bunun GERÇEK ALT
// KÜMESİNİ (3 tür) kabul eder — `ThumbnailMediaType`'ın iki görsel üyesi + `application/pdf`.
// Kalan altı tür (`image/webp`, `image/heic`, `application/msword`,
// `application/vnd.openxmlformats-officedocument.wordprocessingml.document`,
// `application/vnd.ms-excel`, `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`)
// §8 yüklemesi için kabul edilir ama doc-check tarafından hiçbir zaman OKUNAMAZ.

/**
 * doc-check'in intake katmanının kabul ettiği medya türleri —
 * `services/doc-check/src/intake/types.ts` (`IntakeMediaType`) ile BİREBİR. `DOCUMENT_ACCEPTED_TYPES`
 * (9 tür) bunun üst kümesidir; aradaki fark yukarıdaki yorumda listelenir.
 */
export const DOC_CHECK_READABLE_TYPES = ['application/pdf', 'image/png', 'image/jpeg'] as const;

/**
 * Dosya doc-check tarafından analiz edilebilir mi? `DOCUMENT_ACCEPTED_TYPES`'ın altı üyesi —
 * `image/webp`, `image/heic`, `application/msword`,
 * `application/vnd.openxmlformats-officedocument.wordprocessingml.document`,
 * `application/vnd.ms-excel`, `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet` —
 * §8 yüklemesi için kabul edilir ama doc-check'in intake'i yalnız `DOC_CHECK_READABLE_TYPES`'ı
 * kabul ettiğinden hiçbir zaman OKUNAMAZ. Bu altı tür yükleme öncesi burada yakalanmazsa
 * yüklenip hiçbir analiz akışına link'lenemeyen — dolayısıyla hiçbir taşeron listesinde
 * görünmeyen ve asla çöp toplanmayan — kalıcı bir yetim doküman satırı doğar; bu yüzden
 * tasarım bu dosyaları yüklemeden ÖNCE tarayıcıda tutar (bkz. çağıran ekran).
 */
export function isAnalyzableDocumentFile(file: File): boolean {
  return DOC_CHECK_READABLE_TYPES.includes(file.type as (typeof DOC_CHECK_READABLE_TYPES)[number]);
}

// Sunucu sınırlarının BİREBİR aynısı (`backend/src/Isgportal.Contractors/DocCheck/DocCheckOptions.cs`
// — `MaxFilesPerAnalysis`/`MaxTotalBytes` varsayılanları, api-contract §20). Sunucu NİHAİ
// otoritedir; istemci yalnız bilinen bir reddi ÖNDEN gösterir. §8'in genel 20 MB/dosya
// ön-doğrulamasından (`DOCUMENT_MAX_SIZE_BYTES`) AYRI — bu sınırlar yalnız bir analiz
// BATCH'inin toplamına uygulanır.

/** Bir analiz batch'indeki azami dosya sayısı (`DocCheckOptions.MaxFilesPerAnalysis` ile aynı). */
export const MAX_ANALYSIS_FILES = 10;

/** Bir analiz batch'indeki toplam azami bayt (60 MiB; `DocCheckOptions.MaxTotalBytes` ile aynı). */
export const MAX_ANALYSIS_TOTAL_BYTES = 62_914_560;

// ---- Video dosya politikası (§16 Eğitim videosu — büyük dosya) ----
//
// Eğitim videosu belgeden **çok daha büyüktür**; §8 akışı aynıdır (create → S3 PUT →
// confirm) ama boyut/tip ön-doğrulaması ayrıdır. Nihai azami boyut yine backend
// `Settings:Storage:*`'ta (video için daha yüksek eşik) — istemci yalnız gereksiz
// yüklemeyi önden eler. Bu sabitler yalnız **video** yükleyen ekranlarca (eğitim editörü)
// kullanılır; belge yükleyen ekranlar `validateDocumentFile`'ı kullanmaya devam eder.

/** Video ön-doğrulama azami boyutu (500 MB). Backend nihai otoritedir. */
export const VIDEO_MAX_SIZE_BYTES = 500 * 1024 * 1024;

/** Kabul edilen video içerik türleri. Boş/tanınmaz tip → allow-list dışı. */
export const VIDEO_ACCEPTED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'] as const;

/** `<input accept>` için türetilmiş dize (video tür + yaygın uzantılar). */
export const VIDEO_ACCEPT_ATTR = '.mp4,.webm,.mov,video/mp4,video/webm,video/quicktime';

/** İstemci video ön-doğrulaması — hata kodu döner (i18n) ya da `null` (geçerli). */
export function validateVideoFile(file: File): 'VIDEO_TOO_LARGE' | 'VIDEO_TYPE_UNSUPPORTED' | null {
  if (file.size <= 0 || file.size > VIDEO_MAX_SIZE_BYTES) {
    return 'VIDEO_TOO_LARGE';
  }
  // Tip boşsa (bazı tarayıcılar) uzantıya güvenip backend'e bırakırız; tanınıyorsa allow-list.
  if (
    file.type &&
    !VIDEO_ACCEPTED_TYPES.includes(file.type as (typeof VIDEO_ACCEPTED_TYPES)[number])
  ) {
    return 'VIDEO_TYPE_UNSUPPORTED';
  }
  return null;
}

// ============================================================================
// §8 API uçları (metadata + yaşam döngüsü)
// ============================================================================

/**
 * `POST /documents` (`create`) — metadata (`pending`) + presigned PUT URL üretir.
 * Yeni kimlik `value.document.id`/`uuid`. `400 VALIDATION_FAILED` → boş `fileName`/
 * `contentType`, `size` ≤ 0 / azami aşımı (`DOCUMENT_TOO_LARGE`).
 */
export function createDocument(body: DocumentCreateRequest) {
  return api.post<DocumentUploadDto>('documents', { json: body });
}

/**
 * `POST /documents/{uuid}/confirm` (`create`) — yüklemeyi doğrular (S3 HEAD → `confirmed`).
 * Gövde yok. Idempotent (zaten confirmed ise başarı). `409 DOCUMENT_NOT_UPLOADED` →
 * istemci PUT'u tamamlamadı; `400 DOCUMENT_TOO_LARGE` → gerçek boyut sınırı aşıyor.
 */
export function confirmDocument(uuid: string) {
  return api.post<DocumentDto>(`documents/${uuid}/confirm`);
}

/**
 * `GET /documents/{uuid}` (`get`) — üst-veri + kısa ömürlü presigned GET indirme URL'i.
 * `409 DOCUMENT_NOT_CONFIRMED` → pending indirilemez; `404 DOCUMENT_NOT_FOUND` → yok/başka firma.
 */
export function getDocument(uuid: string) {
  return api.get<DocumentDownloadDto>(`documents/${uuid}`);
}

/** `DELETE /documents/{uuid}` (`delete`) — soft-delete + S3 nesnesi silinir. */
export function deleteDocument(uuid: string) {
  return api.delete<Empty>(`documents/${uuid}`);
}

/**
 * Az önce yüklenen (confirmed) bir §8 dosyasını iş modülüne bağlar (`link`). Bağlama KESİN
 * reddedilirse (`4xx`, `isDefinitiveRejection` — ör. tür reddi) dosya best-effort silinir, yetim
 * kalmaz. Ağ hatası ya da `5xx`'te dosya SİLİNMEZ: bağ kaydedilmiş ve yanıt kaybolmuş olabilir;
 * §8.5 DELETE koşulsuzdur, silmek kayıtlı bir bağın dosyasını götürürdü. Asıl hata her durumda
 * yeniden fırlatılır (silme hatası onu maskelemez).
 */
export async function linkUploadedDocument<T>(
  documentUuid: string,
  link: () => Promise<T>,
): Promise<T> {
  try {
    return await link();
  } catch (error) {
    if (isDefinitiveRejection(error)) {
      try {
        await deleteDocument(documentUuid);
      } catch {
        // Silme de düştüyse asıl bağlama hatası gösterilir.
      }
    }
    throw error;
  }
}

// ============================================================================
// İstemci yükleme orkestrasyonu (create → S3 PUT → confirm)
// ============================================================================

/** Yükleme ilerlemesi (0-100). Belirsizse (`lengthComputable=false`) çağrılmaz. */
export type UploadProgress = (percent: number) => void;

export interface UploadDocumentOptions {
  branchId?: number | null;
  siteId?: number | null;
  /** Yükleme ilerlemesi geri-çağrısı (S3 PUT aşaması). */
  onProgress?: UploadProgress;
  /** İptal sinyali (kullanıcı vazgeçerse PUT durdurulur). */
  signal?: AbortSignal;
}

/**
 * Presigned URL'e ham içeriği **PUT** eder (doğrudan S3'e — api client DEĞİL; zarf yok).
 * İlerleme için `XMLHttpRequest` kullanılır (fetch upload-progress'i evrensel değil).
 * S3 2xx dışında yanıt verirse (imza/CORS/boyut) stabil kodlu `ApiError` fırlatılır
 * (`DOCUMENT_UPLOAD_FAILED`) — UI i18n ile temiz mesaj gösterir; ham S3 XML sızmaz.
 */
function putToPresignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  options: UploadDocumentOptions,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);

    if (options.onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          options.onProgress?.(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    if (options.signal) {
      if (options.signal.aborted) {
        xhr.abort();
        reject(uploadError(0, 'DOCUMENT_UPLOAD_ABORTED'));
        return;
      }
      options.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(uploadError(xhr.status, 'DOCUMENT_UPLOAD_FAILED'));
      }
    };
    xhr.onerror = () => reject(uploadError(0, 'DOCUMENT_UPLOAD_FAILED'));
    xhr.onabort = () => reject(uploadError(0, 'DOCUMENT_UPLOAD_ABORTED'));

    xhr.send(file);
  });
}

/** S3 PUT hatasını istemci hata modeline (`ApiError`) çevirir (i18n koduyla). */
function uploadError(statusCode: number, errorCode: string): ApiError {
  return new ApiError({ statusCode, errorCode, message: errorCode });
}

/**
 * Uçtan uca doküman yükleme — api-contract §8 akışı (create → S3 PUT → confirm). Başarıda
 * **confirmed** `DocumentDto` döner; `document.uuid` iş modülüne link'lenir (ör.
 * `contractorsApi.linkContractorDocument`). İçerik sunucudan geçmez.
 *
 * NOT: Çağıran, dosyayı **önce** `validateDocumentFile` ile doğrulamalıdır (boyut/tip).
 * Presigned `uploadUrl` mutlaktır (S3 host'u); api client taban adresi kullanılmaz.
 */
export async function uploadDocument(
  file: File,
  options: UploadDocumentOptions = {},
): Promise<DocumentDto> {
  const contentType = file.type && file.type.length > 0 ? file.type : 'application/octet-stream';

  const upload = await createDocument({
    fileName: file.name,
    contentType,
    size: file.size,
    branchId: options.branchId,
    siteId: options.siteId,
  });

  await putToPresignedUrl(upload.uploadUrl, file, contentType, options);

  return confirmDocument(upload.document.uuid);
}
